import {
  Box,
  Button,
  Grid,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CircularLoading,
  LargeContentBox,
} from "../../components/common";
import { ScreenMobile } from "@/components/common/ScreenMobile";

import { PatientData } from "./components/PatientData";

import { Assignment, PlayArrow } from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useClinicalRegisterContext } from "../../contexts/ClinicalRegisterContext";
import { useSessionContext } from "../../contexts/SessionContext";
import { BundlePatientSummary } from "../../services/fhir";
import { GetAppointment } from "../../services/fhir/appointment/GetAppointment";
import { HTTPResponseStatus } from "../../utils/enum";
import { PatientSummary } from "./components/PatientSummary";
import { PageHeader } from "@/components/common/PageHeader";
import { EhrRecordsLayoutProvider } from "./context/EhrRecordsLayoutContext";
import {
  EhrRecordsScreenProvider,
  type EhrRecordsPushedScreen,
} from "./context/EhrRecordsScreenContext";

export function EHRRecords() {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const { id } = useParams();
  const { state } = useLocation();
  const { user } = useSessionContext();
  const { verifyEncounter } = useClinicalRegisterContext();
  const navigate = useNavigate();
  const [screenOpen, setScreenOpen] = useState(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [summaryData, setSummaryData] = useState();
  const [refetch, setRefetch] = useState<boolean>(false);
  const [appointmentData, setAppointmentData] = useState<any>(null);
  const [pushedScreens, setPushedScreens] = useState<EhrRecordsPushedScreen[]>(
    [],
  );
  const activePushedScreen = pushedScreens[pushedScreens.length - 1];

  const pushScreen = useCallback((screen: EhrRecordsPushedScreen) => {
    setPushedScreens((current) => [...current, screen]);
  }, []);

  const popScreen = useCallback(() => {
    setPushedScreens((current) => current.slice(0, -1));
  }, []);

  const verifyRoute = id === "myrecords" ? user?.username : undefined;
  const canStartAppointment =
    state?.appointment && appointmentData?.status === "booked";

  useEffect(() => {
    async function GetBundle() {
      setLoading(true);
      try {
        const bundleResponse = await BundlePatientSummary({
          identifier: state?.patientId || verifyRoute,
          purposeofuse: state?.purpose || "Atendimento",
        });

        if (bundleResponse.status === HTTPResponseStatus.Forbidden) {
          navigate("/");
          toast.warn("Verifique as opções de consentimento");
        }
        setSummaryData(bundleResponse);
      } catch (err) {
        console.log("PatientSummary error:", err);
      } finally {
        setLoading(false);
      }
    }

    async function FetchAppointment() {
      if (state?.appointment) {
        try {
          const appointmentResponse = await GetAppointment({
            ehrrunner: state?.patientId,
            _id: state.appointment?.id,
          });
          const appointment =
            appointmentResponse?.resourceType === "Appointment"
              ? appointmentResponse
              : appointmentResponse?.entry?.[0]?.resource;

          setAppointmentData(appointment);
        } catch (err) {
          console.log("GetAppointment error:", err);
          setAppointmentData(null);
        }
      }
    }

    if (state?.patientId || verifyRoute) GetBundle();
    FetchAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, state?.patientId, navigate, user]);

  const handleCloseScreen = () => {
    setPushedScreens([]);
    setScreenOpen(false);
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  const startAppointmentButton = canStartAppointment ? (
    <Button
      variant="contained"
      color="primary"
      onClick={async () => await verifyEncounter(state?.appointment)}
      startIcon={<PlayArrow />}
      fullWidth={isMobileLayout}
      sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2.5 }}
    >
      Iniciar atendimento
    </Button>
  ) : null;

  const summaryBlock = loading ? (
    <Stack
      mt={isMobileLayout ? 2 : 4}
      sx={{ minHeight: "200px", justifyContent: "center" }}
    >
      <CircularLoading />
    </Stack>
  ) : (
    <PatientSummary summary={summaryData} setRefetch={setRefetch} />
  );

  const patientDataBlock = (
    <PatientData
      patientId={state?.patientId || verifyRoute}
      summaryData={summaryData}
    />
  );

  const bodyContent = (
    <EhrRecordsScreenProvider pushScreen={pushScreen} popScreen={popScreen}>
      <EhrRecordsLayoutProvider isMobileLayout={isMobileLayout}>
        <Stack spacing={isMobileLayout ? 2 : 3} width="100%">
          {isMobileLayout ? (
            patientDataBlock
          ) : (
            <Grid
              container
              direction="row"
              maxWidth={{
                xs: `${window.innerWidth - 50}px`,
                sm: "fit-content",
              }}
              rowSpacing={3}
              columnSpacing={3}
            >
              {patientDataBlock}
            </Grid>
          )}

          {startAppointmentButton}

          <Box sx={{ width: "100%" }}>{summaryBlock}</Box>
        </Stack>
      </EhrRecordsLayoutProvider>
    </EhrRecordsScreenProvider>
  );

  if (isMobileLayout) {
    return (
      <ScreenMobile
        title={activePushedScreen?.title ?? "Sumário de saúde"}
        open={screenOpen}
        onClose={handleCloseScreen}
        onBack={pushedScreens.length > 0 ? popScreen : undefined}
      >
        {activePushedScreen ? (
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              px: 1.5,
              pb: 2,
              pt: 0.5,
              boxSizing: "border-box",
            }}
          >
            {activePushedScreen.content}
          </Box>
        ) : (
          <Stack
            spacing={1.5}
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              pt: 0.5,
              boxSizing: "border-box",
            }}
          >
            {bodyContent}
          </Stack>
        )}
      </ScreenMobile>
    );
  }

  return (
    <LargeContentBox>
      <Grid container rowSpacing={3} columnSpacing={6}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <PageHeader
            title="Sumário de saúde"
            icon={Assignment}
            action={startAppointmentButton}
          />

          {bodyContent}
        </Grid>
      </Grid>
    </LargeContentBox>
  );
}
